// Every analytics event name and param value used across the app lives
// here — change/add one in this file instead of hunting down every
// trackEvent(...) call site that used the raw string.

export const AnalyticsEvent = {
  // Standard (web/browser) flow.
  PASSWORDLESS_LOGIN_STARTED: "passwordless_login_started",
  PASSWORDLESS_LOGIN_SUCCEEDED: "passwordless_login_succeeded",
  PASSWORDLESS_LOGIN_FAILED: "passwordless_login_failed",
  PASSWORD_LOGIN_STARTED: "password_login_started",
  PASSWORD_LOGIN_SUCCEEDED: "password_login_succeeded",
  PASSWORD_LOGIN_FAILED: "password_login_failed",
  PROFILE_UPDATE_SUCCEEDED: "profile_update_succeeded",
  PROFILE_UPDATE_FAILED: "profile_update_failed",
  LOGOUT: "logout",

  // TV/device pairing (Device Grant) flow — same shape as above, kept as
  // distinct event names so the two flows don't get lumped together.
  DEVICE_PASSWORDLESS_LOGIN_STARTED: "device_passwordless_login_started",
  DEVICE_PASSWORDLESS_LOGIN_SUCCEEDED: "device_passwordless_login_succeeded",
  DEVICE_PASSWORDLESS_LOGIN_FAILED: "device_passwordless_login_failed",
  DEVICE_PASSWORD_LOGIN_STARTED: "device_password_login_started",
  DEVICE_PASSWORD_LOGIN_SUCCEEDED: "device_password_login_succeeded",
  DEVICE_PASSWORD_LOGIN_FAILED: "device_password_login_failed",
  DEVICE_PROFILE_UPDATE_SUCCEEDED: "device_profile_update_succeeded",
  DEVICE_PROFILE_UPDATE_FAILED: "device_profile_update_failed",
  DEVICE_LOGOUT: "device_logout",

  // Device Grant-only steps (no standard-flow equivalent).
  VALIDATE_USER_CODE_SUCCEEDED: "validate_user_code_succeeded",
  VALIDATE_USER_CODE_FAILED: "validate_user_code_failed",
  APPROVE_DEVICE_CODE_SUCCEEDED: "approve_device_code_succeeded",
  DEVICE_GRANT_COMPLETE: "device_grant_complete",

  // How long the user spent on a given screen before navigating away —
  // fired on unmount by useScreenTimeTracking. One event name per screen
  // (instead of one generic event + a screen_name param) so each screen's
  // dwell time can be queried/reported on directly.
  TIME_ON_LOGIN_SCREEN: "time_on_login_screen",
  TIME_ON_OTP_SCREEN: "time_on_otp_screen",
  TIME_ON_PASSWORD_LOGIN_SCREEN: "time_on_password_login_screen",
  TIME_ON_PROFILE_UPDATE_SCREEN: "time_on_profile_update_screen",
  TIME_ON_HOME_SCREEN: "time_on_home_screen",
  TIME_ON_DEVICE_LOGIN_SCREEN: "time_on_device_login_screen",
  TIME_ON_DEVICE_OTP_SCREEN: "time_on_device_otp_screen",
  TIME_ON_DEVICE_PASSWORD_LOGIN_SCREEN: "time_on_device_password_login_screen",
  TIME_ON_DEVICE_PROFILE_UPDATE_SCREEN: "time_on_device_profile_update_screen",
  TIME_ON_DEVICE_HOME_SCREEN: "time_on_device_home_screen",
  TIME_ON_DEVICE_VALIDATE_USER_CODE_SCREEN: "time_on_device_validate_user_code_screen",
  TIME_ON_DEVICE_COMPLETE_SCREEN: "time_on_device_complete_screen",

  // OTP-entry-specific error, in addition to the broader *_LOGIN_FAILED
  // event — lets OTP mistakes be queried separately from other failures
  // (e.g. network errors, expired codes sent from the backend).
  OTP_ERROR: "otp_error",
  DEVICE_OTP_ERROR: "device_otp_error",

  // Duration from the OTP screen mounting to the user submitting a code
  // (regardless of whether that code was correct).
  TIME_TO_ENTER_OTP: "time_to_enter_otp",
  DEVICE_TIME_TO_ENTER_OTP: "device_time_to_enter_otp",

  // Duration from the user first submitting login credentials to actually
  // being authenticated (token issued) — spans the OTP-verification page
  // navigation for the passwordless flow.
  TIME_TO_LOGIN: "time_to_login",
  DEVICE_TIME_TO_LOGIN: "device_time_to_login",
} as const;
export type AnalyticsEvent = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

export const AnalyticsMethod = {
  PASSWORDLESS: "passwordless",
  PASSWORD: "password",
} as const;
export type AnalyticsMethod = (typeof AnalyticsMethod)[keyof typeof AnalyticsMethod];

export const AnalyticsFlow = {
  STANDARD: "standard",
  DEVICE_GRANT: "device_grant",
} as const;
export type AnalyticsFlow = (typeof AnalyticsFlow)[keyof typeof AnalyticsFlow];

export const AnalyticsStatus = {
  STARTED: "started",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;
export type AnalyticsStatus = (typeof AnalyticsStatus)[keyof typeof AnalyticsStatus];

// Which step of the passwordless login attempt an outcome applies to —
// only meaningful for `status: FAILED`, since "start" success just means
// the OTP was sent, not that the user is logged in.
export const AnalyticsStage = {
  START: "start",
  OTP: "otp",
} as const;
export type AnalyticsStage = (typeof AnalyticsStage)[keyof typeof AnalyticsStage];

export const DeviceGrantStep = {
  VALIDATE_USER_CODE: "validate_user_code",
  APPROVE_DEVICE_CODE: "approve_device_code",
  COMPLETE: "complete",
} as const;
export type DeviceGrantStep = (typeof DeviceGrantStep)[keyof typeof DeviceGrantStep];
